import * as React from 'react';
import { useMediaQuery } from '~hooks/useMediaQuery';

const STORAGE_KEY = 'sidebarCollapsed';
const ROOT_ATTR = 'data-sidebar-collapsed';

/** Below this even the 60px rail costs more than the page can spare, so the nav
 *  leaves the layout and becomes an overlay drawer. Kept in step with the
 *  `--sidebar-width: 0` override in src/styles/variables.css. */
const DRAWER_QUERY = '(max-width: 550px)';

/** Whether the nav is showing as the icon rail, and the toggle that flips it.
 *
 *  The choice is the user's and it sticks: someone who works on a laptop
 *  shouldn't have to reclaim the width on every visit. Width alone never
 *  decides it — the rail is a preference, not a breakpoint.
 *
 *  The state also lands on <html> as `data-sidebar-collapsed`, because the
 *  width the rail frees belongs to the page around the nav: the top bar and
 *  the main column are sized off `--sidebar-width`, and they are not this
 *  component's children. */
export const useSidebarCollapse = () => {
  const [collapsed, setCollapsed] = React.useState(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      // Private mode / storage disabled — the nav still works, it just forgets.
      return false;
    }
  });

  React.useEffect(() => {
    document.documentElement.setAttribute(ROOT_ATTR, String(collapsed));
    try {
      window.localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      /* see above */
    }
    return () => document.documentElement.removeAttribute(ROOT_ATTR);
  }, [collapsed]);

  const toggle = React.useCallback(() => setCollapsed((c) => !c), []);

  const offCanvas = useMediaQuery(DRAWER_QUERY);
  // Deliberately not persisted, and deliberately not the same flag as
  // `collapsed`: the rail is a standing preference, an open drawer is a thing
  // you are doing right now. Sharing one flag would either restore the drawer
  // open on load or let opening it rewrite the desktop preference.
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  // Leaving drawer widths mid-session — a rotation, a resized window — would
  // otherwise strand the open state on a nav that is back in the layout.
  React.useEffect(() => {
    if (!offCanvas) setDrawerOpen(false);
  }, [offCanvas]);

  const openDrawer = React.useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = React.useCallback(() => setDrawerOpen(false), []);

  return { collapsed, toggle, offCanvas, drawerOpen, openDrawer, closeDrawer };
};
